/**
 * Dancing Figure Visualizer - WebGL
 *
 * Audio-reactive 3D character that dances to the music.
 * Uses Three.js animation system with additive blending for natural movement.
 *
 * Features:
 * - Beat detection triggers accent moves
 * - Frequency bands map to body parts (bass=hips, mid=torso, high=arms)
 * - Multiple animation modes: clips, procedural, hybrid
 * - Character style options: Xbot robot, stylized human
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { BaseWebGLVisualizer } from '../lib/base-webgl-visualizer.js';
import { VisualizerRegistry } from '../visualizer.js';

// Dev-only logging — no-ops in production to reduce console noise
const _DEV = import.meta.env?.DEV;
function debugLog(...args) { if (_DEV) console.log(...args); }

// Metadata for manifest generation
export const metadata = {
  name: 'dancing-figure',
  displayName: 'Dancing Figure',
  contextType: 'webgl',
};

// Model paths - served from public folder (Vite serves public/ at root)
const MODEL_PATHS = {
  xbot: '/models/Xbot.glb',
};

// Animation clip names from Xbot model
const BASE_ACTIONS = ['idle', 'walk', 'run'];
const ADDITIVE_ACTIONS = ['sneak_pose', 'sad_pose', 'agree', 'headShake'];

class DancingFigureVisualizer extends BaseWebGLVisualizer {
  static name = 'dancing-figure';
  static displayName = 'Dancing Figure';

  static getConfigUI() {
    return [
      // Animation section
      {
        type: 'select', id: 'animationMode', label: 'Animation Mode',
        options: [
          { value: 'clips', label: 'Dance Clips' },
          { value: 'procedural', label: 'Procedural' },
          { value: 'hybrid', label: 'Hybrid' },
        ],
        default: 'procedural',
        section: 'Animation'
      },
      {
        type: 'range', id: 'danceIntensity', label: 'Dance Intensity',
        min: 0, max: 100, default: 50, scale: 0.01,
        section: 'Animation'
      },
      {
        type: 'range', id: 'animationSpeed', label: 'Speed',
        min: 50, max: 200, default: 100, scale: 0.01,
        section: 'Animation'
      },
      {
        type: 'range', id: 'smoothing', label: 'Audio Smoothing',
        min: 0, max: 95, default: 80, scale: 0.01,
        section: 'Animation'
      },

      // Audio Response section
      {
        type: 'checkbox', id: 'reactToBeats', label: 'React to Beats',
        default: true,
        section: 'Audio Response'
      },
      {
        type: 'range', id: 'beatIntensity', label: 'Beat Intensity',
        min: 0, max: 100, default: 70, scale: 0.01,
        section: 'Audio Response'
      },
      // Scene section
      {
        type: 'checkbox', id: 'showFloor', label: 'Show Floor',
        default: true,
        section: 'Scene'
      },
      {
        type: 'checkbox', id: 'spotlightOnBeat', label: 'Spotlight Flash on Beat',
        default: true,
        section: 'Scene'
      },
      {
        type: 'select', id: 'cameraMode', label: 'Camera',
        options: [
          { value: 'fixed', label: 'Fixed' },
          { value: 'orbit', label: 'Auto-Orbit' },
        ],
        default: 'fixed',
        section: 'Scene'
      },
      {
        type: 'range', id: 'cameraDistance', label: 'Camera Distance',
        min: 100, max: 400, default: 180,
        section: 'Scene'
      },
      {
        type: 'color', id: 'backgroundColor', label: 'Background',
        default: '#0a0a12',
        section: 'Scene'
      },

      // Debug section
      {
        type: 'checkbox', id: 'showBeatIndicator', label: 'Show Beat Indicator',
        default: false,
        section: 'Debug'
      },
    ];
  }

  getDefaultConfig() {
    return {
      characterStyle: 'xbot',
      animationMode: 'procedural',
      danceIntensity: 0.5,
      animationSpeed: 1.0,
      smoothing: 0.8,
      reactToBeats: true,
      beatIntensity: 0.7,
      showFloor: true,
      spotlightOnBeat: true,
      cameraMode: 'fixed',
      cameraDistance: 180,
      backgroundColor: '#0a0a12',
      showBeatIndicator: false,
    };
  }

  initScene() {
    // Clock for animation timing
    this.clock = new THREE.Clock();

    // Model state
    this.model = null;
    this.mixer = null;
    this.actions = {};
    this.skeleton = null;
    this.bones = {};
    this.modelLoaded = false;

    // Beat reaction state
    this.beatPulse = 0;
    this.lastBeatTime = 0;

    // Smoothed audio values for body parts
    this.smoothedBass = 0;
    this.smoothedMid = 0;
    this.smoothedHigh = 0;

    // Store original bone rotations for procedural animation
    this.originalRotations = {};

    // Physics state for smooth animation (spring-damper system)
    // Each property tracks: current value, velocity, and target
    this.physics = {
      hipY: { value: 0, velocity: 0, target: 0 },
      hipRotY: { value: 0, velocity: 0, target: 0 },
      hipRotZ: { value: 0, velocity: 0, target: 0 },
      spineRotY: { value: 0, velocity: 0, target: 0 },
      spine1RotY: { value: 0, velocity: 0, target: 0 },
      spine2RotY: { value: 0, velocity: 0, target: 0 },
      leftArmRotZ: { value: 0, velocity: 0, target: 0 },
      leftArmRotX: { value: 0, velocity: 0, target: 0 },
      rightArmRotZ: { value: 0, velocity: 0, target: 0 },
      rightArmRotX: { value: 0, velocity: 0, target: 0 },
      leftForeArmRotY: { value: 0, velocity: 0, target: 0 },
      rightForeArmRotY: { value: 0, velocity: 0, target: 0 },
      headRotX: { value: 0, velocity: 0, target: 0 },
    };

    // Camera setup - centered on model's torso (model will be ~100 units tall after scaling)
    this.cameraTargetY = 50;
    this.camera.fov = 50;
    this.camera.near = 1;
    this.camera.far = 2000;
    this.camera.position.set(0, this.cameraTargetY, this.config.cameraDistance);
    this.camera.lookAt(0, this.cameraTargetY, 0);
    this.camera.updateProjectionMatrix();

    // OrbitControls (non-export mode only)
    if (!this.exportMode) {
      this.controls = new OrbitControls(this.camera, this.canvas);
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.05;
      this.controls.target.set(0, this.cameraTargetY, 0);
      this.controls.minDistance = 80;
      this.controls.maxDistance = 500;
      this.controls.update();
    }

    // Lights
    this.setupLights();

    // Floor
    if (this.config.showFloor) {
      this.createFloor();
    }

    // Beat indicator light (debug feature)
    this.createBeatIndicator();

    // Load character model
    this.loadModel();
  }

  setupLights() {
    // Ambient light for overall illumination
    this.ambientLight = new THREE.AmbientLight(0x404040, 1.5);
    this.scene.add(this.ambientLight);

    // Main directional light
    this.directionalLight = new THREE.DirectionalLight(0xffffff, 2);
    this.directionalLight.position.set(50, 200, 100);
    this.directionalLight.castShadow = true;
    this.directionalLight.shadow.mapSize.width = 1024;
    this.directionalLight.shadow.mapSize.height = 1024;
    this.directionalLight.shadow.camera.near = 10;
    this.directionalLight.shadow.camera.far = 500;
    this.directionalLight.shadow.camera.left = -100;
    this.directionalLight.shadow.camera.right = 100;
    this.directionalLight.shadow.camera.top = 200;
    this.directionalLight.shadow.camera.bottom = -50;
    this.scene.add(this.directionalLight);

    // Fill light from opposite side
    this.fillLight = new THREE.DirectionalLight(0x8888ff, 0.5);
    this.fillLight.position.set(-50, 100, -50);
    this.scene.add(this.fillLight);

    // Spotlight for beat effect
    this.spotlight = new THREE.SpotLight(0xff4488, 0, 400, Math.PI / 6, 0.5);
    this.spotlight.position.set(0, 300, 0);
    this.spotlight.target.position.set(0, 0, 0);
    this.scene.add(this.spotlight);
    this.scene.add(this.spotlight.target);
  }

  createFloor() {
    // Circular floor
    const floorGeometry = new THREE.CircleGeometry(200, 64);
    const floorMaterial = new THREE.MeshStandardMaterial({
      color: 0x222228,
      metalness: 0.3,
      roughness: 0.8,
    });
    this.floor = new THREE.Mesh(floorGeometry, floorMaterial);
    this.floor.rotation.x = -Math.PI / 2;
    this.floor.receiveShadow = true;
    this.scene.add(this.floor);

    // Grid helper for visual reference
    const gridHelper = new THREE.GridHelper(400, 20, 0x333340, 0x222230);
    gridHelper.position.y = 0.1;
    this.scene.add(gridHelper);
    this.gridHelper = gridHelper;
  }

  createBeatIndicator() {
    // Create a glowing sphere that lights up on beats
    const geometry = new THREE.SphereGeometry(5, 16, 16);
    const material = new THREE.MeshBasicMaterial({
      color: 0x00ff00,
      transparent: true,
      opacity: 0.8
    });
    this.beatIndicator = new THREE.Mesh(geometry, material);
    // Position in front of camera, off to the side (will be updated after model loads)
    this.beatIndicator.position.set(-50, 50, 50);
    this.beatIndicator.visible = this.config.showBeatIndicator;
    this.scene.add(this.beatIndicator);
    debugLog('[DancingFigure] Beat indicator created, visible:', this.config.showBeatIndicator);

    // Add a point light inside the indicator for glow effect
    this.beatIndicatorLight = new THREE.PointLight(0x00ff00, 0, 100);
    this.beatIndicatorLight.position.copy(this.beatIndicator.position);
    this.scene.add(this.beatIndicatorLight);
  }

  loadModel() {
    const loader = new GLTFLoader();
    const modelPath = MODEL_PATHS[this.config.characterStyle];

    debugLog('[DancingFigure] Loading model from:', modelPath);

    loader.load(
      modelPath,
      (gltf) => {
        // Check if visualizer was disposed while loading
        if (this.disposed || !this.scene) {
          debugLog('[DancingFigure] Visualizer disposed during model load, aborting');
          return;
        }

        debugLog('[DancingFigure] GLTF loaded, processing...');
        this.model = gltf.scene;

        // Scale model to fit scene (model is ~1.8 units, we want ~100 units)
        const targetHeight = 100;
        const box = new THREE.Box3().setFromObject(this.model);
        const currentHeight = box.max.y - box.min.y;
        const scaleFactor = targetHeight / currentHeight;
        this.model.scale.setScalar(scaleFactor);
        debugLog('[DancingFigure] Scaled model by', scaleFactor.toFixed(1), 'x');

        this.scene.add(this.model);

        // Recalculate bounding box after scaling
        box.setFromObject(this.model);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        debugLog('[DancingFigure] Model bounding box (after scale):', {
          min: { x: box.min.x.toFixed(2), y: box.min.y.toFixed(2), z: box.min.z.toFixed(2) },
          max: { x: box.max.x.toFixed(2), y: box.max.y.toFixed(2), z: box.max.z.toFixed(2) },
          size: { x: size.x.toFixed(2), y: size.y.toFixed(2), z: size.z.toFixed(2) },
          center: { x: center.x.toFixed(2), y: center.y.toFixed(2), z: center.z.toFixed(2) }
        });

        // Configure model
        let meshCount = 0;
        this.model.traverse((child) => {
          if (child.isMesh) {
            child.castShadow = true;
            child.receiveShadow = true;
            meshCount++;
          }
          if (child.isSkinnedMesh) {
            this.skeleton = child.skeleton;
          }
        });
        debugLog('[DancingFigure] Found', meshCount, 'meshes, skeleton:', !!this.skeleton);

        // Store bone references for procedural animation
        this.cacheBoneReferences();
        debugLog('[DancingFigure] Cached', Object.keys(this.bones).length, 'bones');

        // Setup animation mixer
        if (gltf.animations && gltf.animations.length > 0) {
          this.mixer = new THREE.AnimationMixer(this.model);
          this.setupAnimations(gltf.animations);
          debugLog('[DancingFigure] Animation mixer setup with', gltf.animations.length, 'clips');
        }

        // Update camera target based on actual model center
        this.cameraTargetY = center.y;
        this.camera.position.set(0, center.y, this.config.cameraDistance);
        this.camera.lookAt(0, center.y, 0);
        if (this.controls) {
          this.controls.target.set(0, center.y, 0);
          this.controls.update();
        }
        debugLog('[DancingFigure] Camera positioned at y =', center.y.toFixed(2));

        this.modelLoaded = true;
        debugLog('[DancingFigure] Model loaded successfully');
      },
      (progress) => {
        if (progress.total > 0) {
          debugLog('[DancingFigure] Loading progress:', Math.round(progress.loaded / progress.total * 100) + '%');
        }
      },
      (error) => {
        console.error('[DancingFigure] Error loading model:', error);
        // Check if visualizer was disposed while loading
        if (this.disposed || !this.scene) {
          debugLog('[DancingFigure] Visualizer disposed, skipping fallback');
          return;
        }
        debugLog('[DancingFigure] Creating fallback figure...');
        // Create a fallback primitive figure
        this.createFallbackFigure();
      }
    );
  }

  cacheBoneReferences() {
    if (!this.skeleton) {
      debugLog('[DancingFigure] No skeleton found!');
      return;
    }

    // Log ALL bone names in skeleton for debugging
    const allBoneNames = this.skeleton.bones.map(b => b.name);
    debugLog('[DancingFigure] All skeleton bones:', allBoneNames);

    // Common bone names in Mixamo/humanoid rigs
    const boneNames = [
      'mixamorigHips', 'mixamorigSpine', 'mixamorigSpine1', 'mixamorigSpine2',
      'mixamorigNeck', 'mixamorigHead',
      'mixamorigLeftShoulder', 'mixamorigLeftArm', 'mixamorigLeftForeArm', 'mixamorigLeftHand',
      'mixamorigRightShoulder', 'mixamorigRightArm', 'mixamorigRightForeArm', 'mixamorigRightHand',
      'mixamorigLeftUpLeg', 'mixamorigLeftLeg', 'mixamorigLeftFoot',
      'mixamorigRightUpLeg', 'mixamorigRightLeg', 'mixamorigRightFoot',
    ];

    for (const bone of this.skeleton.bones) {
      if (boneNames.includes(bone.name)) {
        this.bones[bone.name] = bone;
        // Store original rotation for procedural animation
        this.originalRotations[bone.name] = bone.rotation.clone();
      }
    }

    debugLog('[DancingFigure] Matched bones:', Object.keys(this.bones));
  }

  setupAnimations(animations) {
    // Setup base and additive actions
    for (const clip of animations) {
      const name = clip.name;

      if (BASE_ACTIONS.includes(name)) {
        const action = this.mixer.clipAction(clip);
        action.play();
        action.setEffectiveWeight(name === 'idle' ? 1 : 0);
        this.actions[name] = { action, weight: name === 'idle' ? 1 : 0 };
      } else if (ADDITIVE_ACTIONS.includes(name)) {
        THREE.AnimationUtils.makeClipAdditive(clip);
        const action = this.mixer.clipAction(clip);
        action.play();
        action.setEffectiveWeight(0);
        this.actions[name] = { action, weight: 0 };
      }
    }
  }

  createFallbackFigure() {
    // Simple geometric figure as fallback when model fails to load
    const group = new THREE.Group();

    // Body (capsule-like)
    const bodyGeometry = new THREE.CapsuleGeometry(20, 60, 8, 16);
    const bodyMaterial = new THREE.MeshStandardMaterial({ color: 0x4488ff, metalness: 0.5, roughness: 0.5 });
    const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
    body.position.y = 80;
    body.castShadow = true;
    group.add(body);
    this.fallbackBody = body;

    // Head
    const headGeometry = new THREE.SphereGeometry(15, 16, 16);
    const head = new THREE.Mesh(headGeometry, bodyMaterial);
    head.position.y = 135;
    head.castShadow = true;
    group.add(head);
    this.fallbackHead = head;

    // Arms (simple cylinders)
    const armGeometry = new THREE.CylinderGeometry(5, 5, 50, 8);
    const leftArm = new THREE.Mesh(armGeometry, bodyMaterial);
    leftArm.position.set(-35, 100, 0);
    leftArm.rotation.z = Math.PI / 6;
    leftArm.castShadow = true;
    group.add(leftArm);
    this.fallbackLeftArm = leftArm;

    const rightArm = new THREE.Mesh(armGeometry, bodyMaterial);
    rightArm.position.set(35, 100, 0);
    rightArm.rotation.z = -Math.PI / 6;
    rightArm.castShadow = true;
    group.add(rightArm);
    this.fallbackRightArm = rightArm;

    // Legs
    const legGeometry = new THREE.CylinderGeometry(7, 7, 60, 8);
    const leftLeg = new THREE.Mesh(legGeometry, bodyMaterial);
    leftLeg.position.set(-12, 30, 0);
    leftLeg.castShadow = true;
    group.add(leftLeg);
    this.fallbackLeftLeg = leftLeg;

    const rightLeg = new THREE.Mesh(legGeometry, bodyMaterial);
    rightLeg.position.set(12, 30, 0);
    rightLeg.castShadow = true;
    group.add(rightLeg);
    this.fallbackRightLeg = rightLeg;

    this.model = group;
    this.scene.add(group);
    this.modelLoaded = true;
    this.isFallbackFigure = true;
  }

  initObjects() {
    // Objects are created in initScene/loadModel
    // This is called by the base class but we handle it differently
  }

  getObjectCount() {
    // We use overall audio energy, not per-object mapping
    return 7; // 7 frequency bands
  }

  updateObjects(amplitudes, features) {
    if (!this.modelLoaded) return;

    const delta = this.clock.getDelta();

    // Debug: log features availability every 60 frames
    if (!this._frameCount) this._frameCount = 0;
    this._frameCount++;
    if (this._frameCount % 60 === 0) {
      debugLog('[DancingFigure] Features check:', {
        hasFeatures: !!features,
        hasRhythm: !!(features?.rhythm),
        rhythmKeys: features?.rhythm ? Object.keys(features.rhythm) : 'N/A'
      });
    }

    // Extract audio features
    let bass = 0, mid = 0, high = 0;
    let isOnset = false, onsetStrength = 0;

    if (features) {
      const bands = features.bands || {};
      bass = (bands.subBass || 0) * 0.5 + (bands.bass || 0) * 0.5;
      mid = (bands.lowMid || 0) * 0.3 + (bands.mid || 0) * 0.4 + (bands.highMid || 0) * 0.3;
      high = (bands.presence || 0) * 0.4 + (bands.brilliance || 0) * 0.6;

      const rhythm = features.rhythm || {};
      isOnset = rhythm.isOnset || false;
      onsetStrength = rhythm.onsetStrength || 0;
    }

    // Smooth audio values
    const smoothFactor = 0.15;
    this.smoothedBass = this.smoothedBass * (1 - smoothFactor) + bass * smoothFactor;
    this.smoothedMid = this.smoothedMid * (1 - smoothFactor) + mid * smoothFactor;
    this.smoothedHigh = this.smoothedHigh * (1 - smoothFactor) + high * smoothFactor;

    // Handle beat detection
    if (isOnset) {
      debugLog('[DancingFigure] Beat detected! strength:', onsetStrength.toFixed(2));
      if (this.config.reactToBeats) {
        this.beatPulse = 1.0;
        this.lastBeatTime = performance.now();
      }
    }

    // Decay beat pulse
    this.beatPulse *= 0.9;

    // Update beat indicator (debug)
    if (this.beatIndicator && this.config.showBeatIndicator) {
      this.beatIndicator.visible = true;
      // Flash bright green on beat, fade to dim
      const indicatorIntensity = isOnset ? 1.0 : this.beatPulse;
      this.beatIndicator.material.opacity = 0.3 + indicatorIntensity * 0.7;
      this.beatIndicator.material.color.setHex(isOnset ? 0x00ff00 : 0x00aa00);
      this.beatIndicatorLight.intensity = indicatorIntensity * 3;
    } else if (this.beatIndicator) {
      this.beatIndicator.visible = false;
    }

    // Update animation based on mode
    if (this.config.animationMode === 'clips' && this.mixer) {
      this.updateClipAnimation(delta);
      // Update mixer only in clips mode
      this.mixer.update(delta * this.config.animationSpeed);
    } else if (this.config.animationMode === 'procedural') {
      // Pure procedural - NO mixer update (it would overwrite our bone changes)
      this.updateProceduralAnimation();
    } else if (this.config.animationMode === 'hybrid' && this.mixer) {
      this.updateClipAnimation(delta);
      // Update mixer first, then apply procedural on top
      this.mixer.update(delta * this.config.animationSpeed);
      this.updateProceduralAnimation();
    }

    // Spotlight beat effect
    if (this.config.spotlightOnBeat) {
      this.spotlight.intensity = this.beatPulse * 5 * this.config.beatIntensity;
    }

    // Camera auto-orbit
    if (this.config.cameraMode === 'orbit' && !this.exportMode) {
      const time = performance.now() * 0.0002;
      const radius = this.config.cameraDistance;
      this.camera.position.x = Math.sin(time) * radius;
      this.camera.position.z = Math.cos(time) * radius;
      this.camera.position.y = this.cameraTargetY;
      this.camera.lookAt(0, this.cameraTargetY, 0);
    }

    // Update controls
    if (this.controls && !this.exportMode) {
      this.controls.update();
    }
  }

  updateClipAnimation(delta) {
    // Map energy to animation weights
    const energy = (this.smoothedBass + this.smoothedMid + this.smoothedHigh) / 3;
    const intensity = this.config.danceIntensity;

    // Adjust base action weights based on energy
    if (this.actions.idle) {
      const idleWeight = Math.max(0, 1 - energy * 2 * intensity);
      this.setActionWeight(this.actions.idle.action, idleWeight);
    }
    if (this.actions.walk) {
      const walkWeight = energy < 0.5 ? energy * 2 * intensity : (1 - energy) * 2 * intensity;
      this.setActionWeight(this.actions.walk.action, walkWeight);
    }
    if (this.actions.run) {
      const runWeight = Math.max(0, (energy - 0.5) * 2 * intensity);
      this.setActionWeight(this.actions.run.action, runWeight);
    }

    // Beat-triggered additive actions
    if (this.beatPulse > 0.5 && this.actions.agree) {
      this.setActionWeight(this.actions.agree.action, this.beatPulse * this.config.beatIntensity);
    }
  }

  updateProceduralAnimation() {
    const intensity = this.config.danceIntensity;
    const beatIntensity = this.config.beatIntensity;
    const time = performance.now() * 0.001;

    if (this.isFallbackFigure) {
      // Animate fallback geometric figure
      this.animateFallbackFigure(time, intensity);
      return;
    }

    // Debug: log bone status once
    if (!this._boneStatusLogged) {
      this._boneStatusLogged = true;
      debugLog('[DancingFigure] Procedural animation check:', {
        hasBones: !!this.bones,
        boneCount: this.bones ? Object.keys(this.bones).length : 0,
        boneNames: this.bones ? Object.keys(this.bones) : [],
        intensity,
        beatIntensity
      });
    }

    // Animate bones directly if we have skeleton
    if (!this.bones || Object.keys(this.bones).length === 0) return;

    // ============================================
    // SIMPLIFIED ANIMATION: Only forearms on beats
    // Everything else stays stationary
    // ============================================

    // First, set up a natural resting pose (arms down) if not done
    if (!this._restPoseSet) {
      this._restPoseSet = true;

      // Rotate upper arms down from T-pose
      // Negative Z for left arm, positive Z for right arm
      const leftArm = this.bones['mixamorigLeftArm'];
      const rightArm = this.bones['mixamorigRightArm'];
      if (leftArm) {
        leftArm.rotation.z = -1.2;  // Rotate down
        debugLog('[DancingFigure] Set left arm to rest pose, rotation.z =', leftArm.rotation.z);
      }
      if (rightArm) {
        rightArm.rotation.z = 1.2; // Rotate down
        debugLog('[DancingFigure] Set right arm to rest pose, rotation.z =', rightArm.rotation.z);
      }
    }

    // Elbow bend amount on beats
    const elbowBendTarget = this.beatPulse * 2.5 * beatIntensity * intensity;

    // Simple exponential smoothing (responsive)
    const smoothFactor = 0.3;
    if (this._elbowBendValue === undefined) this._elbowBendValue = 0;
    this._elbowBendValue = this._elbowBendValue * (1 - smoothFactor) + elbowBendTarget * smoothFactor;

    // Apply to forearm bones - try Z axis for elbow bend
    const leftForeArm = this.bones['mixamorigLeftForeArm'];
    if (leftForeArm) {
      // Z rotation should bend the elbow (pivot around the elbow joint)
      leftForeArm.rotation.z = this._elbowBendValue;
    }

    const rightForeArm = this.bones['mixamorigRightForeArm'];
    if (rightForeArm) {
      // Opposite direction for right arm
      rightForeArm.rotation.z = -this._elbowBendValue;
    }

    // Debug: log values every 60 frames
    if (this._frameCount && this._frameCount % 60 === 0) {
      debugLog('[DancingFigure] Forearm animation:', {
        beatPulse: this.beatPulse.toFixed(3),
        target: elbowBendTarget.toFixed(3),
        smoothed: this._elbowBendValue.toFixed(3),
        leftZ: leftForeArm ? leftForeArm.rotation.z.toFixed(3) : 'N/A',
        rightZ: rightForeArm ? rightForeArm.rotation.z.toFixed(3) : 'N/A',
      });
    }
  }

  animateFallbackFigure(time, intensity) {
    // Animate the simple geometric fallback figure
    if (this.fallbackBody) {
      // Body bounce
      const bounce = this.smoothedBass * 10 * intensity + this.beatPulse * 15 * this.config.beatIntensity;
      this.fallbackBody.position.y = 80 - bounce;

      // Body sway
      this.fallbackBody.rotation.z = Math.sin(time * 2) * this.smoothedMid * 0.2 * intensity;
    }

    if (this.fallbackHead) {
      // Head position follows body
      const bounce = this.smoothedBass * 10 * intensity + this.beatPulse * 15 * this.config.beatIntensity;
      this.fallbackHead.position.y = 135 - bounce;

      // Head bob
      this.fallbackHead.rotation.x = Math.sin(time * 4) * this.smoothedMid * 0.1 * intensity;
    }

    if (this.fallbackLeftArm) {
      // Arm raise based on high frequencies
      const raise = this.smoothedHigh * 0.8 * intensity + this.beatPulse * 0.4 * this.config.beatIntensity;
      this.fallbackLeftArm.rotation.z = Math.PI / 6 - raise;
      this.fallbackLeftArm.rotation.x = Math.sin(time * 5) * this.smoothedHigh * 0.3 * intensity;
    }

    if (this.fallbackRightArm) {
      const raise = this.smoothedHigh * 0.8 * intensity + this.beatPulse * 0.4 * this.config.beatIntensity;
      this.fallbackRightArm.rotation.z = -Math.PI / 6 + raise;
      this.fallbackRightArm.rotation.x = Math.sin(time * 5 + Math.PI) * this.smoothedHigh * 0.3 * intensity;
    }

    // Leg movement (subtle)
    if (this.fallbackLeftLeg) {
      this.fallbackLeftLeg.rotation.x = Math.sin(time * 3) * this.smoothedBass * 0.1 * intensity;
    }
    if (this.fallbackRightLeg) {
      this.fallbackRightLeg.rotation.x = Math.sin(time * 3 + Math.PI) * this.smoothedBass * 0.1 * intensity;
    }
  }

  setActionWeight(action, weight) {
    if (!action) return;
    action.enabled = true;
    action.setEffectiveTimeScale(1);
    action.setEffectiveWeight(weight);
  }

  onConfigChange(changed) {
    // Handle floor visibility
    if ('showFloor' in changed) {
      if (this.floor) {
        this.floor.visible = this.config.showFloor;
      }
      if (this.gridHelper) {
        this.gridHelper.visible = this.config.showFloor;
      }
    }

    // Handle camera distance
    if ('cameraDistance' in changed && this.config.cameraMode === 'fixed') {
      this.camera.position.z = this.config.cameraDistance;
      this.camera.lookAt(0, this.cameraTargetY, 0);
    }

    // Handle animation mode change - reset bone rotations when switching to procedural
    if ('animationMode' in changed) {
      debugLog('[DancingFigure] Animation mode changed to:', this.config.animationMode);

      // Reset bones to original positions when switching modes
      if (this.config.animationMode === 'procedural' || this.config.animationMode === 'hybrid') {
        this.resetBoneRotations();
      }

      // Stop all clip actions when switching to procedural
      if (this.config.animationMode === 'procedural' && this.mixer) {
        for (const actionData of Object.values(this.actions)) {
          if (actionData.action) {
            actionData.action.setEffectiveWeight(0);
          }
        }
      }

      // Re-enable idle for clips mode
      if (this.config.animationMode === 'clips' && this.actions.idle) {
        this.actions.idle.action.setEffectiveWeight(1);
      }
    }

    // Handle beat indicator visibility
    if ('showBeatIndicator' in changed) {
      if (this.beatIndicator) {
        this.beatIndicator.visible = this.config.showBeatIndicator;
      }
    }

  }

  resetBoneRotations() {
    // Reset all bones to their original rotations
    for (const [boneName, bone] of Object.entries(this.bones)) {
      const original = this.originalRotations[boneName];
      if (original && bone) {
        bone.rotation.copy(original);
      }
    }
    debugLog('[DancingFigure] Reset bone rotations');
  }

  disposeObjects() {
    // Dispose model
    if (this.model) {
      this.model.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(m => m.dispose());
          } else {
            child.material.dispose();
          }
        }
      });
      this.scene.remove(this.model);
    }

    // Dispose floor
    if (this.floor) {
      this.floor.geometry.dispose();
      this.floor.material.dispose();
      this.scene.remove(this.floor);
    }

    // Dispose grid
    if (this.gridHelper) {
      this.gridHelper.geometry.dispose();
      this.gridHelper.material.dispose();
      this.scene.remove(this.gridHelper);
    }

    // Dispose lights (shadow map must be disposed before removing)
    if (this.ambientLight) this.scene.remove(this.ambientLight);
    if (this.directionalLight) {
      this.directionalLight.shadow?.map?.dispose();
      this.scene.remove(this.directionalLight);
    }
    if (this.fillLight) this.scene.remove(this.fillLight);
    if (this.spotlight) {
      this.scene.remove(this.spotlight);
      this.scene.remove(this.spotlight.target);
    }

    // Dispose beat indicator
    if (this.beatIndicator) {
      this.beatIndicator.geometry.dispose();
      this.beatIndicator.material.dispose();
      this.scene.remove(this.beatIndicator);
    }
    if (this.beatIndicatorLight) {
      this.scene.remove(this.beatIndicatorLight);
    }

    // Dispose controls
    if (this.controls) {
      this.controls.dispose();
    }

    // Stop mixer
    if (this.mixer) {
      this.mixer.stopAllAction();
    }
  }
}

// Register with the visualizer registry
VisualizerRegistry.register(DancingFigureVisualizer);

export { DancingFigureVisualizer };
export default DancingFigureVisualizer;
